import { create } from "zustand";
import { chatApi, type ChatMessage } from "../lib/api";
import { streamChat } from "../lib/sse";
import { useAuthStore } from "./authStore";

interface ChatSession {
  activityId: string;
  messages: ChatMessage[];
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
}

interface ChatState {
  // Sessions per activity
  sessions: Record<string, ChatSession>;

  // Actions
  openChat: (activityId: string, provider?: string) => Promise<void>;
  closeChat: (activityId: string) => void;
  sendMessage: (activityId: string, content: string, provider?: string) => Promise<void>;
  clearChat: (activityId: string) => Promise<void>;
  clearLocalSession: (activityId: string) => void;
  checkAndOpenIfHasHistory: (activityId: string, provider?: string) => Promise<void>;
  reset: () => void;
}

const createEmptySession = (activityId: string): ChatSession => ({
  activityId,
  messages: [],
  isOpen: false,
  isLoading: false,
  error: null,
});

export const useChatStore = create<ChatState>((set, get) => ({
  sessions: {},

  openChat: async (activityId: string, provider?: string) => {
    const session = get().sessions[activityId] || createEmptySession(activityId);

    // Set chat as open and loading
    set((state) => ({
      sessions: {
        ...state.sessions,
        [activityId]: {
          ...session,
          isOpen: true,
          isLoading: true,
          error: null,
        },
      },
    }));

    // Load chat history from backend
    try {
      const { messages } = await chatApi.getHistory(activityId, provider);
      set((state) => ({
        sessions: {
          ...state.sessions,
          [activityId]: {
            ...state.sessions[activityId],
            messages,
            isLoading: false,
          },
        },
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load chat";
      set((state) => ({
        sessions: {
          ...state.sessions,
          [activityId]: {
            ...state.sessions[activityId],
            isLoading: false,
            error: message,
          },
        },
      }));
    }
  },

  closeChat: (activityId: string) => {
    set((state) => ({
      sessions: {
        ...state.sessions,
        [activityId]: {
          ...(state.sessions[activityId] || createEmptySession(activityId)),
          isOpen: false,
        },
      },
    }));
  },

  sendMessage: async (activityId: string, content: string, provider?: string) => {
    const session = get().sessions[activityId];
    if (!session) return;

    const token = useAuthStore.getState().session?.access_token;
    if (!token) return;

    // Create optimistic user message
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content,
      timestamp: new Date().toISOString(),
    };

    // Create streaming assistant placeholder
    const streamingId = `streaming-${Date.now()}`;
    const assistantPlaceholder: ChatMessage = {
      id: streamingId,
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
    };

    // Add both messages and set loading
    set((state) => ({
      sessions: {
        ...state.sessions,
        [activityId]: {
          ...session,
          messages: [...session.messages, userMessage, assistantPlaceholder],
          isLoading: true,
          error: null,
        },
      },
    }));

    const params = provider ? `?provider=${provider}` : "";

    try {
      await streamChat(
        `/chat/${activityId}${params}`,
        { message: content },
        token,
        {
          onChunk: (text) => {
            set((state) => {
              const s = state.sessions[activityId];
              if (!s) return state;
              return {
                sessions: {
                  ...state.sessions,
                  [activityId]: {
                    ...s,
                    messages: s.messages.map((m) =>
                      m.id === streamingId
                        ? { ...m, content: m.content + text }
                        : m
                    ),
                  },
                },
              };
            });
          },
          onDone: () => {
            set((state) => {
              const s = state.sessions[activityId];
              if (!s) return state;
              return {
                sessions: {
                  ...state.sessions,
                  [activityId]: { ...s, isLoading: false },
                },
              };
            });
          },
          onError: (error) => {
            set((state) => {
              const s = state.sessions[activityId];
              if (!s) return state;
              return {
                sessions: {
                  ...state.sessions,
                  [activityId]: {
                    ...s,
                    messages: s.messages.filter(
                      (m) => m.id !== streamingId && m.id !== userMessage.id
                    ),
                    isLoading: false,
                    error: error.message,
                  },
                },
              };
            });
          },
        },
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to send message";
      set((state) => ({
        sessions: {
          ...state.sessions,
          [activityId]: {
            ...state.sessions[activityId],
            messages: state.sessions[activityId].messages.filter(
              (m) => m.id !== streamingId && m.id !== userMessage.id
            ),
            isLoading: false,
            error: message,
          },
        },
      }));
    }
  },

  clearChat: async (activityId: string) => {
    try {
      await chatApi.clearHistory(activityId);
      set((state) => ({
        sessions: {
          ...state.sessions,
          [activityId]: {
            ...(state.sessions[activityId] || createEmptySession(activityId)),
            messages: [],
            error: null,
          },
        },
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to clear chat";
      set((state) => ({
        sessions: {
          ...state.sessions,
          [activityId]: {
            ...state.sessions[activityId],
            error: message,
          },
        },
      }));
    }
  },

  clearLocalSession: (activityId: string) => {
    set((state) => ({
      sessions: {
        ...state.sessions,
        [activityId]: createEmptySession(activityId),
      },
    }));
  },

  checkAndOpenIfHasHistory: async (activityId: string, provider?: string) => {
    const session = get().sessions[activityId];

    // If already open or already has messages loaded, skip
    if (session?.isOpen || (session?.messages && session.messages.length > 0)) {
      return;
    }

    // Fetch history to check if there are messages
    try {
      const { messages } = await chatApi.getHistory(activityId, provider);

      // Only open if there are existing messages
      if (messages.length > 0) {
        set((state) => ({
          sessions: {
            ...state.sessions,
            [activityId]: {
              activityId,
              messages,
              isOpen: true,
              isLoading: false,
              error: null,
            },
          },
        }));
      }
    } catch {
      // Silently fail - don't show error for auto-check
    }
  },

  reset: () => {
    set({ sessions: {} });
  },
}));
